import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { GhostButton } from 'src/components/widgets/buttons';
import { Dialog, DialogWindow } from 'src/components/widgets/dialog';
import { FormControl } from 'src/components/widgets/formControl';
import { AdvancedSelect } from 'src/components/widgets/select';
import Stack from 'src/components/widgets/stack/stack';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { T, stringFormat } from 'src/shared/helpers';
import type { ColorAttribute } from './helpers';
import { getApplicableVariantMappingRepairs, type ChannelMappingRepair } from './variantMappingRepair';
import type { RepairStrategyID } from './variantMappingRepairStrategies/types';

const RepairDialogWindow = styled(DialogWindow)`
    box-sizing: border-box;
    max-height: min(680px, calc(100dvh - 80px));
    max-width: 100%;
    padding: 24px;
    overflow: hidden;

    > h1,
    > div:last-child {
        flex-shrink: 0;
    }
`;

const Preview = styled.div`
    isolation: isolate;

    table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
    }
    th,
    td {
        padding: 8px 12px;
        text-align: left;
        vertical-align: top;
        border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
        overflow-wrap: anywhere;
    }
    th {
        position: sticky;
        top: 0;
        background: white;
        z-index: 1;
    }
    th:first-child {
        width: 22%;
    }
`;

const StrategyList = styled(Stack)`
    flex-wrap: wrap;
    align-items: center;
`;

const StrategyTag = styled.span`
    padding: 4px 8px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 4px;
    font-size: 12px;
`;

const CountButton = styled(GhostButton)`
    min-width: 0;
    text-decoration: underline;
`;

const ManualRepairs = styled.details`
    summary {
        cursor: pointer;
        font-weight: 600;
        padding: 8px 0;
    }
`;

const getStrategyDetails = (id: RepairStrategyID) => {
    switch (id) {
        case 'shopify-option-order':
            return {
                label: T._('Shopify option reorder', 'Admin'),
                description: T._('Match options by name after a swap, such as Size / Color to Color / Size. Values stay with their option.', 'Admin')
            };
        case 'shopify-option-positions':
            return {
                label: T._('Shopify option shifts / removals', 'Admin'),
                description: T._('Update option positions, such as 2 to 1, and remove deleted options only when the remaining mapping is unambiguous.', 'Admin')
            };
        case 'casing':
            return {
                label: T._('Match store casing', 'Admin'),
                description: T._('Match the capitalization of store option names, value labels and IDs, such as RED to Red.', 'Admin')
            };
    }
};

const StrategyBadges: FC<{ strategies: { id: RepairStrategyID; count?: number }[] }> = ({ strategies }) => (
    <StrategyList $horizontal $gap='4px'>
        {strategies.map(({ id, count }) => {
            const { label, description } = getStrategyDetails(id);
            const explanation =
                count === undefined ? description : `${description} ${stringFormat(T._('Applies to {0} variant mappings.', 'Admin'), count.toString())}`;
            return (
                <ZTooltip key={id} title={explanation}>
                    <StrategyTag aria-label={explanation}>{count === undefined ? label : `${label} (${count})`}</StrategyTag>
                </ZTooltip>
            );
        })}
    </StrategyList>
);

const formatLabel = (label: string, id: string) => (label === String(id) ? label : `${label} (${id})`);

const MappingPreview: FC<{ attributes: ColorAttribute[] }> = ({ attributes }) => (
    <Stack $gap='4px'>
        {attributes.map((attribute, index) => (
            <div key={index}>{`${formatLabel(attribute.Label, attribute.Id)}: ${formatLabel(attribute.Value.Label, attribute.Value.Id)}`}</div>
        ))}
    </Stack>
);

const buildChannelPreview = (channel: ChannelMappingRepair) => {
    const repairable = getApplicableVariantMappingRepairs(channel.repairs);
    const repairableKeys = new Set(repairable.map((repair) => repair.key));
    return {
        channel,
        repairable,
        manualRepairs: channel.repairs.filter((repair) => !repairableKeys.has(repair.key)),
        strategyPreviews: channel.availableStrategies
            .map((id) => ({
                id,
                count: repairable.filter((repair) => repair.steps.some((step) => step.strategyID === id)).length
            }))
            .filter((strategy) => strategy.count > 0)
    };
};

type ChannelPreview = ReturnType<typeof buildChannelPreview>;

const ChangesTable: FC<{ repairs: ChannelPreview['repairable'] }> = ({ repairs }) => (
    <table>
        <thead>
            <tr>
                <th scope='col'>{T._('Variant', 'Admin')}</th>
                <th scope='col'>{T._('Before', 'Admin')}</th>
                <th scope='col'>{T._('After', 'Admin')}</th>
                <th scope='col'>{T._('Strategy', 'Admin')}</th>
            </tr>
        </thead>
        <tbody>
            {repairs.map((repair) => (
                <tr key={repair.key}>
                    <td>{repair.name}</td>
                    <td>
                        <MappingPreview attributes={repair.before} />
                    </td>
                    <td>
                        <MappingPreview attributes={repair.after} />
                    </td>
                    <td>
                        <StrategyBadges strategies={repair.steps.map((step) => ({ id: step.strategyID }))} />
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const ManualChangesTable: FC<{ repairs: ChannelPreview['manualRepairs'] }> = ({ repairs }) => (
    <table>
        <thead>
            <tr>
                <th scope='col'>{T._('Variant', 'Admin')}</th>
                <th scope='col'>{T._('Current mapping', 'Admin')}</th>
                <th scope='col'>{T._('Reason', 'Admin')}</th>
            </tr>
        </thead>
        <tbody>
            {repairs.map((repair) => (
                <tr key={repair.key}>
                    <td>{repair.name}</td>
                    <td>{repair.before.length ? <MappingPreview attributes={repair.before} /> : T._('Invalid mapping', 'Admin')}</td>
                    <td>
                        {repair.reason === 'duplicate'
                            ? T._('Another variant would use the same combination.', 'Admin')
                            : repair.reason === 'invalid'
                              ? T._('The saved mapping cannot be read.', 'Admin')
                              : T._('No unambiguous match was found in the store.', 'Admin')}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const ExpandedPreview = styled(Preview)`
    max-height: 280px;
    overflow: auto;
`;

const allChannelsValue = 'all-sales-channels';

const RepairVariantMappingsDialog: FC<{
    channels: ChannelMappingRepair[];
    allOptionsReady: boolean;
    onConfirm: (channels: ChannelMappingRepair[], allChannels: boolean) => void;
}> = ({ channels, allOptionsReady, onConfirm }) => {
    const [selectedChannel, setSelectedChannel] = useState(channels.length > 1 ? allChannelsValue : channels[0].salesChannelID);
    const [expandedPreview, setExpandedPreview] = useState<{ channelID: string; manual: boolean } | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const togglePreview = (channelID: string, manual = false) => {
        setExpandedPreview((current) => (current?.channelID === channelID && current.manual === manual ? null : { channelID, manual }));
    };
    const selectChannel = (channelID: string) => {
        setSelectedChannel(channelID);
        setExpandedPreview(null);
    };
    useEffect(() => {
        contentRef.current?.scrollTo({ top: 0 });
    }, [selectedChannel]);
    const manualExplanation = T._(
        'No safe automatic fix was found. These mappings stay unchanged; use the variant edit button to fix them individually.',
        'Admin'
    );
    const previews = useMemo(() => channels.map(buildChannelPreview), [channels]);
    const { channel, repairable, manualRepairs, strategyPreviews } =
        previews.find((preview) => preview.channel.salesChannelID === selectedChannel) ?? previews[0];
    const allChannels = selectedChannel === allChannelsValue;
    const repairableChannels = previews.filter((preview) => preview.repairable.length > 0);
    const hasManualRepairs = previews.some((preview) => preview.manualRepairs.length > 0);
    const totalRepairs = repairableChannels.reduce((total, preview) => total + preview.repairable.length, 0);
    const channelOptions = [
        { value: allChannelsValue, label: T._('All sales channels', 'Admin') },
        ...previews.map(({ channel, repairable }) => ({
            value: channel.salesChannelID,
            label: stringFormat(T._('{0} - {1} variants to repair', 'Admin'), channel.label, repairable.length.toString())
        }))
    ];
    return (
        <Dialog
            ref={contentRef}
            title={T._('Repair variant mappings', 'Admin')}
            size='large'
            windowDecorator={RepairDialogWindow}
            buttons={[
                {
                    label: allChannels
                        ? stringFormat(T._('Repair {0} mappings across {1} channels', 'Admin'), totalRepairs.toString(), repairableChannels.length.toString())
                        : stringFormat(T._('Repair {0} variants', 'Admin'), repairable.length.toString()),
                    disabled: allChannels ? totalRepairs === 0 || !allOptionsReady : repairable.length === 0,
                    onClick: () => onConfirm(allChannels ? repairableChannels.map((preview) => preview.channel) : [channel], allChannels)
                }
            ]}
        >
            <Stack $gap='12px'>
                <FormControl label={T._('Sales channel', 'Admin')}>
                    <AdvancedSelect
                        options={channelOptions}
                        value={channelOptions.find((option) => option.value === selectedChannel)}
                        onChange={(option) => option && selectChannel(option.value)}
                        menuPosition='fixed'
                    />
                </FormControl>
                {allChannels ? (
                    <Stack $gap='12px'>
                        {!allOptionsReady && (
                            <div>
                                {T._(
                                    'Some sales channels have not been checked. Finish loading or retry failed channels, then reopen this dialog to repair all channels.',
                                    'Admin'
                                )}
                            </div>
                        )}
                        <Preview>
                            <table>
                                <thead>
                                    <tr>
                                        <th scope='col'>{T._('Sales channel', 'Admin')}</th>
                                        <th scope='col'>{T._('Strategies to apply', 'Admin')}</th>
                                        <th scope='col'>{T._('Mappings to fix', 'Admin')}</th>
                                        {hasManualRepairs && (
                                            <th scope='col'>
                                                <ZTooltip title={manualExplanation}>
                                                    <span>{T._('Needs manual fix', 'Admin')}</span>
                                                </ZTooltip>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previews.map((preview) => (
                                        <React.Fragment key={preview.channel.salesChannelID}>
                                            <tr>
                                                <td>
                                                    <GhostButton $size='small' onClick={() => togglePreview(preview.channel.salesChannelID)}>
                                                        {preview.channel.label}
                                                    </GhostButton>
                                                </td>
                                                <td>
                                                    {preview.strategyPreviews.length > 0 ? (
                                                        <StrategyBadges strategies={preview.strategyPreviews} />
                                                    ) : (
                                                        T._('Manual editing required', 'Admin')
                                                    )}
                                                </td>
                                                <td>
                                                    {preview.repairable.length > 0 ? (
                                                        <CountButton
                                                            type='button'
                                                            $size='small'
                                                            aria-label={stringFormat(
                                                                T._('View {0} mapping changes for {1}', 'Admin'),
                                                                preview.repairable.length.toString(),
                                                                preview.channel.label
                                                            )}
                                                            aria-expanded={
                                                                expandedPreview?.channelID === preview.channel.salesChannelID && !expandedPreview.manual
                                                            }
                                                            onClick={() => togglePreview(preview.channel.salesChannelID)}
                                                        >
                                                            {preview.repairable.length}
                                                        </CountButton>
                                                    ) : (
                                                        0
                                                    )}
                                                </td>
                                                {hasManualRepairs && (
                                                    <td>
                                                        {preview.manualRepairs.length > 0 ? (
                                                            <CountButton
                                                                type='button'
                                                                $size='small'
                                                                aria-label={stringFormat(
                                                                    T._('View {0} mappings needing a manual fix for {1}', 'Admin'),
                                                                    preview.manualRepairs.length.toString(),
                                                                    preview.channel.label
                                                                )}
                                                                aria-expanded={
                                                                    expandedPreview?.channelID === preview.channel.salesChannelID && expandedPreview.manual
                                                                }
                                                                onClick={() => togglePreview(preview.channel.salesChannelID, true)}
                                                            >
                                                                {preview.manualRepairs.length}
                                                            </CountButton>
                                                        ) : (
                                                            0
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                            {expandedPreview?.channelID === preview.channel.salesChannelID && (
                                                <tr>
                                                    <td colSpan={hasManualRepairs ? 4 : 3}>
                                                        <ExpandedPreview>
                                                            <Stack $gap='8px'>
                                                                <strong>{stringFormat(T._('Preview for {0}', 'Admin'), preview.channel.label)}</strong>
                                                                {expandedPreview.manual ? (
                                                                    <>
                                                                        <div>{manualExplanation}</div>
                                                                        <ManualChangesTable repairs={preview.manualRepairs} />
                                                                    </>
                                                                ) : (
                                                                    <ChangesTable repairs={preview.repairable} />
                                                                )}
                                                            </Stack>
                                                        </ExpandedPreview>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </Preview>
                        {hasManualRepairs && <div>{manualExplanation}</div>}
                        <div>
                            {T._(
                                'Select a mapping count to expand its preview. Repairs still apply to all sales channels. Save the product after repairing.',
                                'Admin'
                            )}
                        </div>
                    </Stack>
                ) : (
                    <Stack $gap='12px'>
                        {strategyPreviews.length > 0 && (
                            <StrategyList $horizontal $gap='8px'>
                                <strong>{T._('Strategies to apply:', 'Admin')}</strong>
                                <StrategyBadges strategies={strategyPreviews} />
                            </StrategyList>
                        )}
                        <Preview>
                            <Stack $gap='12px'>
                                {repairable.length > 0 && <ChangesTable repairs={repairable} />}
                                {manualRepairs.length > 0 && (
                                    <ManualRepairs key={selectedChannel}>
                                        <summary>
                                            {stringFormat(
                                                T._('{0} variants need manual editing and will remain unchanged', 'Admin'),
                                                manualRepairs.length.toString()
                                            )}
                                        </summary>
                                        <div>{manualExplanation}</div>
                                        <ManualChangesTable repairs={manualRepairs} />
                                    </ManualRepairs>
                                )}
                            </Stack>
                        </Preview>
                        <div>{T._('Applies to the selected channel only. Save the product after repairing.', 'Admin')}</div>
                    </Stack>
                )}
            </Stack>
        </Dialog>
    );
};

export default RepairVariantMappingsDialog;
